// ---------------------------------------------------------------------------
// AUFNAHME-EINGABE — ein Bauteil fuer alle Spielmodi (14.08.2026)
//
// WOZU
// Bis heute hatte jeder Spielmodus seine eigene Eingabe, und sie sprachen
// verschiedene Sprachen:
//
//   501      Board ODER Dart-Tastatur, drei Darts einzeln       (richtig)
//   Party    Dart-Tastatur - eine WORTGLEICHE Kopie des 501er    (doppelt)
//   Bob's    "Wie oft getroffen?" 0x/1x/2x/3x                    (Trefferzahl)
//   RTW      dasselbe                                            (Trefferzahl)
//   Random   Trefferknoepfe                                      (Trefferzahl)
//   121      Ziffernblock, eine Summe                            (Summe)
//   Gotcha   Ziffernblock                                        (Summe)
//   Scoring  Ziffernblock                                        (Summe)
//
// Der Tracker kann aber nur eines liefern: EINZELNE DARTS. Solange ein Modus
// nur "2 getroffen" oder "60" entgegennimmt, laesst er sich nicht anschliessen,
// ohne dass jemand die Darts vorher zu einer Zahl zusammenrechnet - und genau
// dabei geht verloren, was die Statistik braucht.
//
// Deshalb kehrt dieses Bauteil die Richtung um: Es sammelt immer DREI DARTS,
// und der Modus leitet daraus seine Zahl ab.
//
//     Summe        = Σ points          (501, 121, Gotcha, Party, Scoring)
//     Trefferzahl  = darts.filter(...) (Bob's 27, RTW, Random)
//     Einzelfeld   = die Liste selbst  (Cricket, Secure)
//
// EIN DART SIEHT IMMER GLEICH AUS - egal woher er kommt:
//
//     { points, base, multiplier, label, ring, xMm, yMm, quelle }
//
// Das ist dieselbe Form, die board-input.js seit dem 501-Umbau erzeugt und
// die der Tracker im throw-Payload schickt. `quelle` ist "hand" oder
// "erkannt"; Koordinaten sind null, wenn von Hand ueber die Tastatur getippt.
//
// WARUM DAS BAUTEIL SEIN MARKUP SELBST ERZEUGT
// Die Alternative waere, in jedes der neun Partials rund 200 Zeilen Knoepfe
// zu kopieren - dieselbe Kopiererei, die Party und 501 auseinanderlaufen
// liess. So braucht ein Modus im HTML genau eine Zeile:
//
//     <div id="aufnahme-party"></div>
//
// WAS ES NICHT TUT
// Es wertet nicht. Es kennt keine Regel eines Spielmodus - kein Bust, kein
// Checkout, kein Ziel. Es sammelt Darts und uebergibt sie. Die Wertung
// bleibt, wo sie hingehoert: in der einen Funktion des Modus, die es schon
// gibt. Genau deshalb ist der Umbau je Modus klein.
// ---------------------------------------------------------------------------

import { t, tf } from "./i18n.js";

const SLOTS_VORGABE = 3;
const MODUS_KEY = "secure-darts:aufnahme-eingabe";

/** Beschriftung und Farbe eines Darts - beides haengt am Ring, nicht am
 *  Punktwert. 60 kann T20 oder 20+20+20 sein; die Anzeige muss den
 *  Unterschied zeigen, sonst ist die Einzeldart-Eingabe umsonst. */
function dartFarbe(dart) {
  if (!dart) return "";
  if (dart.label === "MISS") return "#888";
  if (dart.multiplier === 3 || dart.base === 50) return "var(--accent-red)";
  if (dart.multiplier === 2 || dart.base === 25) return "var(--accent-green)";
  return "white";
}

/** Dart aus Tastatureingabe. Ohne Koordinaten - die gibt es nur beim
 *  Boardklick und beim Tracker. */
function dartAusTastatur(base, mult) {
  // Bull, Bullseye und Miss haben keinen eigenen Multiplikator. Ohne diese
  // Zeile erzeugt ein stehengebliebenes "TRIPLE" ein T25 mit 75 Punkten -
  // ein Feld, das es auf keinem Board gibt.
  const m = base === 25 || base === 50 || base === 0 ? 1 : mult;
  let label = String(base);
  let ring = "single";
  if (base === 0) {
    label = "MISS";
    ring = "miss";
  } else if (base === 25) {
    label = "BULL";
    ring = "bull";
  } else if (base === 50) {
    label = "B-EYE";
    ring = "bullseye";
  } else if (m === 2) {
    label = "D" + base;
    ring = "double";
  } else if (m === 3) {
    label = "T" + base;
    ring = "triple";
  }
  return {
    points: base * m,
    base,
    multiplier: m,
    label,
    ring,
    xMm: null,
    yMm: null,
    quelle: "hand",
  };
}

/**
 * Eine Aufnahme-Eingabe erzeugen und in `ziel` rendern.
 *
 * @param {object} conf
 * @param {string}   conf.modus       Schluessel: DOM-Praefix, localStorage, Adapter
 * @param {Function} conf.ziel        () => HTMLElement, in den gerendert wird
 * @param {Function} conf.onAufnahme  (darts, summe) => boolean|Promise<boolean>
 *                                    false = abgelehnt, die Darts bleiben stehen
 * @param {Function} [conf.onDart]    (dart, summe, anzahl) => "fertig"|null
 *                                    Optionaler Pro-Dart-Rueckruf; "fertig"
 *                                    beendet die Aufnahme sofort (Bust/Finish).
 * @param {Function} [conf.gesperrt]  () => boolean, z. B. Gegner am Zug
 * @param {Function} [conf.aktiv]     () => boolean, Modus sichtbar?
 * @param {number}   [conf.slots]     Vorgabe 3
 * @param {Function} [conf.zielFelder] () => [{base, mult?}] - Felder, die auf
 *                                    dem Board hervorgehoben werden (Bob's,
 *                                    RTW). mult weggelassen = jeder Ring.
 * @param {Function} [conf.bilanz]    (darts) => {name, wert} - was in der
 *                                    Zeile ueber dem Board steht. Vorgabe ist
 *                                    "Aufnahme <Summe>"; Trefferzaehler-Modi
 *                                    brauchen dort die TREFFER, nicht die
 *                                    Punktsumme (60 sagt bei Bob's nichts).
 */
/** Zaehlt als Checkout-Dart: Doppel oder Bullseye (Double-Out).
 *
 *  EINE Stelle statt vier (15.08.2026): Dieselbe Regel stand wortgleich in
 *  game-121.js und game-random.js und sollte für Party und Gotcha ein
 *  drittes und viertes Mal entstehen. Bei einer Regel, die in jedem Modus
 *  gleich lauten MUSS, sind vier Kopien vier Stellen, an denen sie
 *  auseinanderlaufen kann.
 *
 *  Das aeussere Bull (25) ist ein Single und zaehlt NICHT; das Bullseye (50)
 *  ist das Doppel des Bull und zaehlt. `multiplier` UND `mult` werden
 *  gelesen - Handdarts fuehren das eine, der Tracker-Payload das andere. */
export function istCheckoutDart(dart) {
  if (!dart) return false;
  const mult = Number(dart.multiplier ?? dart.mult ?? 1);
  return mult === 2 || Number(dart.base) === 50;
}

/** Der letzte Dart einer Aufnahme - oder null, wenn keine Liste vorliegt.
 *
 *  Ohne Liste (alter Aufrufer, Bot, Test) bleibt die Double-Out-Pruefung
 *  bewusst aus: stillschweigend haerter zu werten waere schlimmer als die
 *  Luecke. */
export function letzterDart(wuerfe) {
  return Array.isArray(wuerfe) && wuerfe.length ? wuerfe[wuerfe.length - 1] : null;
}

export function erzeugeAufnahmeEingabe(conf) {
  const modus = conf.modus;
  const slots = conf.slots || SLOTS_VORGABE;
  const gesperrt = conf.gesperrt || (() => false);
  const aktiv = conf.aktiv || (() => true);

  const zustand = {
    darts: [],
    gewaehlt: null, // Index des Darts, der als Naechstes ersetzt wird
    mult: 1,
    ansicht: "board", // "board" | "tastatur"
    board: null,
    wurzel: null,
    gebaut: false,
  };

  try {
    const gemerkt = localStorage.getItem(`${MODUS_KEY}:${modus}`);
    if (gemerkt === "board" || gemerkt === "tastatur") zustand.ansicht = gemerkt;
  } catch (error) {
    /* gesperrter Speicher - dann gilt die Vorgabe */
  }

  const id = (teil) => `aufnahme-${modus}-${teil}`;
  const el = (teil) => document.getElementById(id(teil));

  // ── Markup ───────────────────────────────────────────────────────────────

  function baue() {
    const wurzel = typeof conf.ziel === "function" ? conf.ziel() : conf.ziel;
    if (!wurzel || zustand.gebaut) return Boolean(zustand.gebaut);

    const zahlKnoepfe = [];
    for (let n = 1; n <= 20; n++) {
      zahlKnoepfe.push(
        `<button type="button" class="ae-zahl" data-base="${n}">${n}</button>`,
      );
    }

    wurzel.innerHTML = `
      <div class="ae" id="${id("wurzel")}">
        <!-- Klassen aus dem 501 (autoscore-darts/-dart/-sum): Die drei
             Darts sollen in JEDEM Modus gleich aussehen und gleich heissen.
             Eigene Klassen waeren ein zweites Aussehen, das irgendwann
             auseinanderlaeuft - genau wie die zwei Rechner vorher. -->
        <div class="autoscore-darts" id="${id("slots")}"></div>

        <!-- Hinweiszeile. Sie traegt genau eine Aufgabe: bei laufendem
             Tracker sagen, DASS man korrigieren kann und WIE. Ohne sie ist
             die Korrektur zwar da, aber unsichtbar - eine Kachel sieht nicht
             aus wie ein Knopf. -->
        <div class="ae-hinweis" id="${id("hinweis")}"></div>

        <div class="ae-kopf">
          <div class="autoscore-sum">
            <span id="${id("bilanzname")}">${t("501game.aufnahme", "Aufnahme")}</span>
            <b id="${id("summe")}">0</b>
          </div>
          <button type="button" class="btn-glass ae-umschalter" id="${id("umschalter")}"></button>
        </div>

        <div class="ae-board" id="${id("board")}"></div>

        <div class="ae-tastatur" id="${id("tastatur")}">
          <!-- data-i18n auf den festen Beschriftungen: Beim Sprachwechsel
               laeuft uebersetzeDom() ueber das Dokument und erwischt sie
               damit von selbst. Ohne das Attribut stuenden hier bis zum
               Neuladen der Seite deutsche Knoepfe. -->
          <div class="ae-mult">
            <button type="button" class="ae-m" data-mult="1" data-i18n="501game.013">${t("501game.013", "SINGLE")}</button>
            <button type="button" class="ae-m" data-mult="2" data-i18n="501game.014">${t("501game.014", "DOUBLE")}</button>
            <button type="button" class="ae-m" data-mult="3" data-i18n="501game.015">${t("501game.015", "TRIPLE")}</button>
          </div>
          <div class="ae-zahlen">
            ${zahlKnoepfe.join("")}
            <button type="button" class="ae-zahl ae-miss" data-base="0" data-i18n="501game.016">${t("501game.016", "MISS")}</button>
            <button type="button" class="ae-zahl ae-bull" data-base="25">25</button>
            <button type="button" class="ae-zahl ae-beye" data-base="50">50</button>
          </div>
        </div>

        <div class="ae-aktionen">
          <button type="button" class="btn-glass" id="${id("zurueck")}" data-i18n="app.dartZurueck">${t("app.dartZurueck", "Dart zurück")}</button>
          <button type="button" class="btn-glass is-primary" id="${id("uebernehmen")}" data-i18n="501game.012">${t("501game.012", "Next")}</button>
        </div>
      </div>`;

    zustand.wurzel = el("wurzel");

    // Ereignisse gebuendelt am Container statt je Knopf: Die Zahlengitter
    // haben 23 Knoepfe, und sie werden nie neu erzeugt - ein Zuhoerer reicht.
    el("tastatur").addEventListener("click", (ereignis) => {
      const m = ereignis.target.closest("[data-mult]");
      if (m) {
        setzeMult(Number(m.dataset.mult));
        return;
      }
      const z = ereignis.target.closest("[data-base]");
      if (z) nimmDart(dartAusTastatur(Number(z.dataset.base), zustand.mult));
    });

    el("slots").addEventListener("click", (ereignis) => {
      const s = ereignis.target.closest("[data-slot]");
      if (s) waehle(Number(s.dataset.slot));
    });

    el("umschalter").addEventListener("click", () => {
      setzeAnsicht(zustand.ansicht === "board" ? "tastatur" : "board");
    });
    el("zurueck").addEventListener("click", zurueck);
    el("uebernehmen").addEventListener("click", uebernimm);

    // Das echte Board (dartboard.js) - dieselbe Geometrie, in der auch der
    // Tracker rechnet. Nur so liegen Hand- und Trackermarken auf demselben
    // Punkt; das stilisierte Board aus ui.js kann das nicht.
    if (typeof window.createDartboard === "function") {
      zustand.board = window.createDartboard(el("board"), {
        onThrow: (treffer) =>
          nimmDart({ ...treffer, quelle: "hand", xMm: treffer.xMm, yMm: treffer.yMm }),
      });
    }

    // Sprachwechsel: Zwei Beschriftungen setzt JavaScript, nicht das Markup -
    // die Bilanzzeile ("Aufnahme"/"Treffer"/"Rest") und der Umschalter. Sie
    // haengen an Werten, die sich waehrend des Spiels aendern, koennen also
    // kein festes data-i18n tragen. Ohne diesen Zuhoerer blieben genau sie
    // beim Umschalten auf Englisch deutsch stehen.
    window.addEventListener("secure-darts:sprache", () => {
      if (!zustand.gebaut) return;
      zeichne();
      aktualisiereSichtbarkeit();
    });

    zustand.gebaut = true;
    setzeMult(1);
    setzeAnsicht(zustand.ansicht);
    zeichne();
    aktualisiereZiel();
    return true;
  }

  // ── Anzeige ──────────────────────────────────────────────────────────────

  function zeichne() {
    if (!zustand.gebaut) return;
    const kasten = el("slots");
    let summe = 0;
    let html = "";
    for (let i = 0; i < slots; i++) {
      const dart = zustand.darts[i];
      if (dart) summe += dart.points;
      // Fehlwuerfe werden durchnummeriert (M1, M2, M3) - dreimal "MISS"
      // untereinander sagt nicht, der wievielte Dart daneben ging. Nur die
      // ANZEIGE aendert sich; das gespeicherte label bleibt "MISS".
      const text = dart ? (dart.label === "MISS" ? "M" + (i + 1) : dart.label) : "–";
      // data-state traegt dieselben Werte wie im 501: leer / hand / erkannt.
      // Dadurch greifen die vorhandenen Regeln (grauer Text bei leer,
      // gruener Rahmen bei erkannt) ohne eine einzige neue Farbe.
      const status = dart ? dart.quelle || "hand" : "leer";
      html +=
        `<div class="autoscore-dart${zustand.gewaehlt === i ? " is-gewaehlt" : ""}"` +
        ` data-state="${status}" data-slot="${i}" role="button" tabindex="0"` +
        ` style="${dart ? `color:${dartFarbe(dart)}` : ""}">` +
        `<span>${text}</span>${dart ? `<small>${dart.points}</small>` : ""}</div>`;
    }
    kasten.innerHTML = html;

    // Die Zeile ueber dem Board. Vorgabe ist die Punktsumme; ein
    // Trefferzaehler-Modus ersetzt sie durch seine eigene Zahl. Der Baustein
    // wertet dabei NICHT - er fragt den Modus, was dort stehen soll.
    let name = t("501game.aufnahme", "Aufnahme");
    let wert = summe;
    if (typeof conf.bilanz === "function") {
      const eigen = conf.bilanz(zustand.darts.slice());
      if (eigen) {
        if (eigen.name != null) name = eigen.name;
        if (eigen.wert != null) wert = eigen.wert;
      }
    }
    const nameFeld = el("bilanzname");
    if (nameFeld) nameFeld.textContent = name;
    el("summe").textContent = String(wert);
  }

  /** Zielfelder auf dem Board hervorheben. Wird beim Zielwechsel vom Modus
   *  gerufen - der Baustein kennt die Zielreihenfolge nicht. */
  function aktualisiereZiel() {
    if (!zustand.board || typeof zustand.board.zeigeZiel !== "function") return 0;
    const felder = typeof conf.zielFelder === "function" ? conf.zielFelder() : null;
    return zustand.board.zeigeZiel(felder);
  }

  function zeichneBoardMarken() {
    if (!zustand.board) return;
    zustand.board.clear();
    for (const d of zustand.darts) {
      if (Number.isFinite(d.xMm) && Number.isFinite(d.yMm)) {
        zustand.board.mark(d.xMm, d.yMm, d.label, d.quelle);
      }
    }
  }

  function setzeMult(m) {
    zustand.mult = m;
    if (!zustand.gebaut) return;
    for (const knopf of el("tastatur").querySelectorAll("[data-mult]")) {
      knopf.classList.toggle("is-an", Number(knopf.dataset.mult) === m);
    }
  }

  function setzeAnsicht(ansicht) {
    zustand.ansicht = ansicht;
    try {
      localStorage.setItem(`${MODUS_KEY}:${modus}`, ansicht);
    } catch (error) {
      /* dann gilt sie bis zum Neuladen */
    }
    if (!zustand.gebaut) return;
    aktualisiereSichtbarkeit();
  }

  /** Genau EINE Eingabeflaeche zeigen - und bei laufendem Tracker nur die
   *  Korrektur.
   *
   *  Dieselbe Regel wie im 501 (trackerWertet501): Wertet der Tracker, ist ER
   *  die Eingabe. Das Board bleibt trotzdem stehen, weil die erkannten Darts
   *  hineingezeichnet werden - ohne es fehlt die Sichtkontrolle, wo der Pfeil
   *  gelandet ist. Weg gehen nur die Bedienelemente.
   *
   *  ERGAENZUNG 15.08.2026 - die Korrektur:
   *  Bisher war "weg" endgueltig. Eine Kachel liess sich zwar antippen, aber
   *  danach gab es nichts, womit man den Dart haette ersetzen koennen:
   *  Tastatur ausgeblendet, Scheibe nicht klickbar, und der Korrektureditor
   *  des Autoscore-Kastens haengt nur an den drei Kacheln des 501. Der
   *  Tracker war damit in diesen Modi unwidersprechlich.
   *
   *  Jetzt gilt: Ist bei laufendem Tracker eine Kachel gewaehlt, kommt die
   *  Tastatur als KORREKTUREDITOR zurueck - und geht nach dem Ersetzen
   *  wieder. Sie ist dann keine zweite Eingabe (die Frage vom 14.08.), weil
   *  sie sich auf genau einen, bereits erkannten Dart bezieht. */
  function aktualisiereSichtbarkeit() {
    if (!zustand.gebaut) return;
    const tracker = trackerWertet();
    const korrigiert = tracker && zustand.gewaehlt !== null;
    const board = el("board");
    const tastatur = el("tastatur");
    const umschalter = el("umschalter");

    const boardSichtbar = tracker || zustand.ansicht === "board";
    board.style.display = boardSichtbar ? "block" : "none";
    // Die Scheibe bleibt auch beim Korrigieren reine Anzeige: Sie ist die
    // Flaeche, in die der Tracker zeichnet. Korrigiert wird ueber die
    // Tastatur - genau wie im Editor des 501, der auch ein Knopfgitter ist.
    board.classList.toggle("is-nur-anzeige", tracker);
    tastatur.style.display =
      korrigiert || (!tracker && zustand.ansicht === "tastatur") ? "block" : "none";
    tastatur.classList.toggle("is-korrektur", korrigiert);

    // Ein Umschalter ohne Wirkung ist schlimmer als keiner.
    umschalter.style.display = tracker ? "none" : "";
    umschalter.textContent =
      zustand.ansicht === "board"
        ? t("js.g501.037", "Tastatur")
        : t("js.g501.036", "Board");

    const hinweis = el("hinweis");
    if (hinweis) {
      hinweis.textContent = !tracker
        ? ""
        : korrigiert
          ? tf("aufnahme.korrekturFeld", { p1: zustand.gewaehlt + 1 })
          : t("aufnahme.korrekturHinweis", "Zum Korrigieren einen Dart antippen");
      hinweis.dataset.aktiv = korrigiert ? "1" : "0";
      hinweis.style.display = tracker ? "block" : "none";
    }

    // Die Aktionen bleiben: Auch im Trackerbetrieb muss man eine Aufnahme
    // uebernehmen oder einen Dart zuruecknehmen koennen, wenn die Erkennung
    // danebenlag. Das Ziehen der Pfeile uebernimmt sonst automatisch.
  }

  function trackerWertet() {
    const s = window.autoScore && window.autoScore.state;
    return Boolean(s && s.active && s.wertend && s.connection === "verbunden");
  }

  /** Eine Korrektur an den Tracker zurueckmelden (Hard-Case-Signal).
   *
   *  Der 501 tut das seit dem 13.08. ueber seinen Editor. Ohne diese Zeilen
   *  waere in allen anderen Modi zwar korrigierbar, aber der Tracker
   *  erfuehre nie davon - und genau diese Faelle sind die wertvollsten
   *  Trainingsdaten, weil sie die sind, bei denen er danebenlag. */
  function meldeKorrekturAnTracker(stelle, dart) {
    if (typeof window.autoScore?.meldeKorrektur !== "function") return;
    window.autoScore.meldeKorrektur(
      stelle,
      Number(dart.base ?? 0),
      Number(dart.multiplier ?? dart.mult ?? 1),
      Number(dart.points ?? 0),
    );
  }

  // ── Darts sammeln ────────────────────────────────────────────────────────

  function waehle(i) {
    if (gesperrt()) return;
    zustand.gewaehlt = zustand.gewaehlt === i ? null : i;
    zeichne();
    // Bei laufendem Tracker haengt die Tastatur an dieser Auswahl - erneutes
    // Antippen derselben Kachel bricht die Korrektur wieder ab.
    aktualisiereSichtbarkeit();
  }

  /** EINE Sammelstelle fuer Boardklick, Tastatur und Tracker.
   *
   *  Dass alle drei hier durchlaufen, ist der Kern des Bauteils: Eine
   *  Sperre, eine Korrekturregel, eine Slotverwaltung. Getrennte Wege waeren
   *  drei Stellen, an denen dieselbe Bedingung vergessen werden kann. */
  function nimmDart(dart) {
    if (!dart) return null;

    // BEFUND 14.08.2026: Ein Dart ohne brauchbaren Punktwert hat frueher die
    // GANZE Aufnahme vergiftet - `summe` wurde NaN, die Bilanzzeile zeigte
    // "NaN", und der Modus haette diesen Wert weitergerechnet. Ein einzelner
    // unvollstaendiger Trackerdart darf nicht die Wertung kippen; er wird
    // hier verworfen, alles andere laeuft weiter.
    if (!Number.isFinite(Number(dart.points))) {
      console.warn("aufnahme-eingabe: Dart ohne Punktwert verworfen", dart);
      return null;
    }
    dart = { ...dart, points: Number(dart.points) };
    if (!dart.label) dart.label = dart.points === 0 ? "MISS" : String(dart.points);

    // Trackerdarts duerfen NICHT an der Sperre scheitern: Der Gegner am Zug
    // ist ein Grund, die Handeingabe zu sperren - der Tracker meldet dann
    // ohnehin nichts, und wenn doch, gehoert es angezeigt statt verschluckt.
    if (dart.quelle !== "erkannt" && gesperrt()) return null;

    if (zustand.gewaehlt !== null) {
      const stelle = zustand.gewaehlt;
      const alt = zustand.darts[stelle];
      // Was einen erkannten Dart ersetzt, ist eine KORREKTUR - nicht einfach
      // Handeingabe. Der Unterschied ist sichtbar (eigene Rahmenfarbe, die
      // es im Stylesheet schon gab) und er ist die Information, die der
      // Tracker fuers Training braucht.
      if (alt && alt.quelle === "erkannt" && dart.quelle !== "erkannt") {
        dart = { ...dart, quelle: "korrigiert" };
        meldeKorrekturAnTracker(stelle, dart);
      }
      zustand.darts[stelle] = dart;
      zustand.gewaehlt = null;
    } else {
      if (zustand.darts.length >= slots) return null;
      zustand.darts.push(dart);
    }

    setzeMult(1); // nach jedem Dart zurueck auf Single
    zeichne();
    zeichneBoardMarken();
    // Schliesst den Korrektureditor wieder - er gehoerte zu genau dem Dart,
    // der eben ersetzt wurde.
    aktualisiereSichtbarkeit();

    if (typeof conf.onDart === "function") {
      const summe = zustand.darts.reduce((a, d) => a + d.points, 0);
      const antwort = conf.onDart(dart, summe, zustand.darts.length);
      if (antwort === "fertig") return "fertig";
    }
    return null;
  }

  function zurueck() {
    if (gesperrt()) return;
    zustand.darts.pop();
    zustand.gewaehlt = null;
    zeichne();
    zeichneBoardMarken();
  }

  async function uebernimm() {
    if (gesperrt()) return false;
    if (!zustand.darts.length) return false;
    const wuerfe = zustand.darts.slice();
    const summe = wuerfe.reduce((a, d) => a + d.points, 0);

    // REIHENFOLGE: Erst entscheidet das Spiel, DANN wird geleert. Andersherum
    // waeren die Darts bei einer Ablehnung (Gegner am Zug, ungueltiger Wert)
    // kommentarlos weg - derselbe Befund wie bei boardCommit501 am 09.08.
    const angenommen = await conf.onAufnahme(wuerfe, summe);
    if (angenommen === false) {
      zeichne();
      return false;
    }
    leere();
    return true;
  }

  function leere() {
    zustand.darts = [];
    zustand.gewaehlt = null;
    setzeMult(1);
    zeichne();
    if (zustand.board) zustand.board.clear();
  }

  // ── Tracker ──────────────────────────────────────────────────────────────

  const api = {
    baue,
    leere,
    zustand: () => ({ darts: zustand.darts.slice(), ansicht: zustand.ansicht }),
    aktualisiereSichtbarkeit,
    aktualisiereZiel,
    zeichne,
    nimmDart,
    uebernimm,
  };

  // Der Adapter ist die Anbindung an autoscore.js. Er meldet `eigeneSlots`,
  // damit der Autoscore-Kasten seine drei Kacheln NICHT zusaetzlich zeigt -
  // die stehen hier schon, und zwei Reihen Darts nebeneinander waeren genau
  // die Doppeldeutigkeit, die im 501 abgestellt wurde.
  window.autoScoreAdapter = window.autoScoreAdapter || {};
  window.autoScoreAdapter[modus] = {
    korrigierbar: true,
    eigeneSlots: true,
    aktiv: () => Boolean(aktiv() && baue()),
    ziel: () => (typeof conf.ziel === "function" ? conf.ziel() : conf.ziel),
    // autoscore.js ruft das bei jeder Zustandsaenderung - so bemerkt das
    // Bauteil auch einen Tracker, der MITTEN im Spiel an- oder ausgeht.
    sichtbarkeit: aktualisiereSichtbarkeit,
    dart: (dart) => nimmDart({ ...dart, quelle: "erkannt" }),
    aufnahme: async (_summe, _wuerfe) => {
      // autoscore.js uebergibt seine eigene Liste; wir haben dieselben Darts
      // bereits ueber dart() bekommen und in den Slots stehen. Uebernommen
      // wird deshalb UNSERE Liste - sonst gaebe es zwei Wahrheiten darueber,
      // was geworfen wurde.
      if (!zustand.darts.length) return false;
      return uebernimm();
    },
    live: (darts) => {
      if (zustand.board && typeof zustand.board.setLive === "function") {
        zustand.board.setLive(darts);
      }
    },
    markiere: (dart) => {
      if (zustand.board && dart && dart.heat) {
        zustand.board.mark(dart.heat.x_mm, dart.heat.y_mm, dart.label, "erkannt");
      }
    },
    geleert: () => {
      if (zustand.board && typeof zustand.board.setLive === "function") {
        zustand.board.setLive([]);
      }
      zeichneBoardMarken();
    },
  };

  return api;
}

if (typeof window !== "undefined") {
  Object.assign(window, { erzeugeAufnahmeEingabe });
}
